<template>
  <div>
    <div class="table-top-pagination">
      <v-btn
        icon="mdi-chevron-left"
        variant="outlined"
        class="pagination-arrow"
        :disabled="!canGoPrev"
        @click="goPrev"
      />
      <span class="table-item-count">
        {{ displayedItemsStart }}~{{ displayedItemsEnd }}/{{ totalCountLabel }}
      </span>
      <v-btn
        icon="mdi-chevron-right"
        variant="outlined"
        class="pagination-arrow"
        :disabled="!canGoNext"
        @click="goNext"
      />
    </div>
    <v-card variant="outlined">
      <v-data-table
        v-model:page="currentPage"
        :headers="headers"
        :items="items"
        :items-per-page="itemsPerPage"
        class="elevation-0 face-sheet-table"
        hide-default-footer
        no-data-text="データがありません"
      >
        <template #[`item.id`]="{ item }">
          <router-link
            class="face-sheet-id-link"
            :to="{ name: 'faceSheetsDetail', params: { user_id: item.id } }"
          >
            {{ item.id }}
          </router-link>
        </template>
        <template #[`item.status`]="{ item }">
          <div class="status-badges">
            <v-chip
              v-if="item.actionRequired"
              size="small"
              variant="flat"
              class="status-badge status-badge-required"
            >
              要対応
            </v-chip>
            <v-chip
              v-if="item.watching"
              size="small"
              variant="flat"
              class="status-badge status-badge-watch"
            >
              要注意観察中
            </v-chip>
            <v-chip
              v-if="!item.actionRequired && !item.watching"
              size="small"
              variant="flat"
              class="status-badge status-badge-none"
            >
              なし
            </v-chip>
          </div>
        </template>
        <template #[`item.updatedAt`]="{ item }">
          {{ item.updatedAt || "-" }}
        </template>
        <template #bottom />
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "FaceSheetSearchResults",
  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    itemsPerPage: {
      type: Number,
      required: true
    },
    page: {
      type: Number,
      required: true
    },
    capped: {
      type: Boolean,
      default: false
    },
    maxResults: {
      type: Number,
      default: 100
    }
  },
  emits: ["update:page"],
  computed: {
    currentPage: {
      get() {
        return this.page;
      },
      set(value) {
        this.$emit("update:page", value);
      }
    },
    totalItems() {
      return this.items.length;
    },
    // 上限（maxResults）で打ち切られた場合は「N件以上」と表示する。
    totalCountLabel() {
      if (this.capped) return `${this.maxResults}件以上`;
      return `${this.totalItems}件`;
    },
    pageCount() {
      return Math.max(Math.ceil(this.totalItems / this.itemsPerPage), 1);
    },
    displayedItemsStart() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    displayedItemsEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
    canGoPrev() {
      return this.currentPage > 1;
    },
    canGoNext() {
      return this.currentPage < this.pageCount;
    }
  },
  methods: {
    goPrev() {
      if (!this.canGoPrev) return;
      this.currentPage -= 1;
    },
    goNext() {
      if (!this.canGoNext) return;
      this.currentPage += 1;
    }
  }
};
</script>

<style lang="scss" scoped>
.face-sheet-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}

.face-sheet-table :deep(td),
.face-sheet-table :deep(thead th) {
  overflow-wrap: anywhere;
  white-space: pre-line;
  word-break: break-word;
}

.face-sheet-table :deep(thead th) {
  background-color: #dbf4f3 !important;
  font-weight: bold !important;
}

.table-top-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 8px;
}

.table-item-count {
  color: rgba(0, 0, 0, 0.75);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
}

.pagination-arrow {
  border-color: #3acac9 !important;
  border-radius: 8px !important;
  color: #3acac9 !important;
  height: 32px !important;
  min-width: 32px !important;
  width: 32px !important;
}

.pagination-arrow:deep(.v-icon) {
  font-size: 1.1rem;
}

.pagination-arrow.v-btn--disabled {
  border-color: #b0e5e5 !important;
  color: #b0e5e5 !important;
}

.face-sheet-id-link {
  text-decoration: underline;
}

.face-sheet-table :deep(.status-badges) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.face-sheet-table :deep(.status-badge) {
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0;
}

.face-sheet-table :deep(.status-badge-required) {
  background-color: #f9e8ee;
  color: #e64779;
}

.face-sheet-table :deep(.status-badge-watch) {
  background-color: #fbeee3;
  color: #e97a1d;
}

.face-sheet-table :deep(.status-badge-none) {
  background-color: #e5e5e7;
  color: #6f6f73;
}
</style>
